'use client';

import { useEffect, useRef } from 'react';

/**
 * Cloudflare Turnstile widget.
 *
 * Rendered on demand, not on page load: the API decides per request whether a
 * challenge is required (Builder-API/utils/loginRisk.js) and answers with
 * `403 CAPTCHA_REQUIRED`, so a normal sign-in never shows one. The contact form
 * is the exception and mounts it immediately — see that controller for why it
 * cannot be risk-gated.
 *
 * `action` binds the token to the form it was minted on and is asserted
 * server-side, so a token obtained from the public contact widget cannot be
 * spent against /auth/login.
 */

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      reset: (id?: string) => void;
      remove: (id?: string) => void;
    };
  }
}

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || '';

/**
 * One in-flight promise for the whole page. Two widgets mounting together
 * (login + a future form) must not inject the script twice, and a second
 * <script> tag for an already-loaded SDK is a silent source of duplicate
 * callbacks.
 */
let scriptPromise: Promise<void> | null = null;
function loadScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const el = document.createElement('script');
    el.src = SCRIPT_SRC;
    el.async = true;
    el.defer = true;
    el.onload = () => resolve();
    el.onerror = () => {
      // Let a later mount retry rather than caching the failure forever —
      // this fires on a flaky network as readily as on a blocked domain.
      scriptPromise = null;
      reject(new Error('turnstile-script-failed'));
    };
    document.head.appendChild(el);
  });
  return scriptPromise;
}

export default function Turnstile({
  action,
  onToken,
  resetSignal = 0,
  onUnavailable,
}: {
  action: string;
  onToken: (token: string) => void;
  /** Bump to clear a spent token — Turnstile tokens are single-use. */
  resetSignal?: number;
  /**
   * Called when the widget could not be put on screen at all: no site key in
   * this build, or Cloudflare's script never loaded (corporate proxy, blocklist,
   * dead network). NOT called for a failed or expired challenge — that is a
   * widget the reader can see and retry.
   *
   * The distinction is the whole point. A form that asks for a security check it
   * never rendered, and then refuses the submission because the check is
   * missing, is unusable in a way the reader cannot diagnose or work around; the
   * contact form lost real enquiries that way. The caller tells the server this
   * happened so the submission can be accepted and flagged instead of dropped.
   */
  onUnavailable?: (reason: 'no-site-key' | 'script-failed') => void;
}) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const widgetId = useRef<string | null>(null);
  /**
   * The callback is held in a ref so the render effect can depend on nothing
   * but `action`. A parent that passes an inline arrow (every one of ours
   * does) would otherwise re-render the widget on each keystroke, which both
   * resets the challenge mid-solve and leaks widget instances.
   */
  const onTokenRef = useRef(onToken);
  const onUnavailableRef = useRef(onUnavailable);
  /**
   * Synced in an effect, not during render — assigning `ref.current` in a
   * render body is impure and React's lint rule rightly rejects it.
   *
   * Declared BEFORE the widget effect below on purpose: effects run in
   * declaration order, so this one has already copied the current callbacks by
   * the time the widget mounts and can fire one.
   */
  useEffect(() => {
    onTokenRef.current = onToken;
    onUnavailableRef.current = onUnavailable;
  });

  useEffect(() => {
    if (!SITE_KEY) {
      console.warn('[turnstile] NEXT_PUBLIC_TURNSTILE_SITE_KEY is not set — widget not rendered.');
      onUnavailableRef.current?.('no-site-key');
      return;
    }
    let cancelled = false;
    loadScript()
      .then(() => {
        if (cancelled || !hostRef.current || !window.turnstile) return;
        widgetId.current = window.turnstile.render(hostRef.current, {
          sitekey: SITE_KEY,
          action,
          callback: (token: string) => onTokenRef.current(token),
          // A token that expires before submit is worse than no token: the
          // form would post a string the server rejects as stale. Clear it.
          'expired-callback': () => onTokenRef.current(''),
          'error-callback': () => onTokenRef.current(''),
          theme: 'light',
        });
      })
      .catch(() => {
        /* Script blocked or offline. The server fails open unless it is in
           panic mode, so a sign-in can still succeed; swallow rather than
           break the form — but tell the caller, which is how the contact form
           keeps an enquiry it would otherwise have had to refuse. */
        if (!cancelled) onUnavailableRef.current?.('script-failed');
      });
    return () => {
      cancelled = true;
      if (widgetId.current && window.turnstile) window.turnstile.remove(widgetId.current);
      widgetId.current = null;
    };
  }, [action]);

  useEffect(() => {
    if (resetSignal && widgetId.current && window.turnstile) {
      window.turnstile.reset(widgetId.current);
    }
  }, [resetSignal]);

  if (!SITE_KEY) return null;
  return (
    <>
      {/* Cloudflare renders the widget as a fixed 300x65 iframe, and that is
          not negotiable through the API. On a 320px phone the contact form's
          content box is about 232px wide, so the widget pushed the document
          25px wider than the window and the whole page scrolled sideways —
          on the one form the business actually depends on.

          Scaled rather than clipped: the reader has to be able to see and
          click the checkbox, so hiding the overflow would trade a layout bug
          for a conversion bug. `transform-origin: left top` keeps it aligned
          with the fields above it, and the wrapper height is set to the
          scaled height so the transform does not leave a gap under it.

          The breakpoint is 380px rather than a round number because 300px of
          widget plus the form's 48px of padding and the section's 40px needs
          388px of viewport to fit unscaled. */}
      <style dangerouslySetInnerHTML={{ __html: `
        .bsn-turnstile { max-width: 100%; }
        @media (max-width: 380px) {
          .bsn-turnstile { height: 50px; }
          .bsn-turnstile > * { transform: scale(0.76); transform-origin: left top; }
        }
      ` }} />
      <div ref={hostRef} className="bsn-turnstile mt-1" />
    </>
  );
}
